import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Users, Zap, Play } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const mockSpaces = [
  {
    id: 1,
    title: "Tesla Engineering Insights: FSD Evolution",
    host: { name: "Alex Tesla", avatar: "AT", title: "Senior Engineer" },
    status: "live",
    scheduledTime: "Started 15 mins ago",
    attendees: 2340,
    tags: ["Tesla", "FSD", "Engineering"],
    queue: 145,
    description: "Deep dive into latest FSD improvements, challenges, and future roadmap.",
    streamUrl: "#",
  },
  {
    id: 2,
    title: "SpaceX Mission Planning AMA",
    host: { name: "Mars Mission Lead", avatar: "ML", title: "Mission Director" },
    status: "scheduled",
    scheduledTime: "Starts in 2 hours",
    attendees: 890,
    tags: ["SpaceX", "Starship", "Mars"],
    queue: 0,
    description: "Ask anything about Starship development, timelines, and future missions.",
    streamUrl: "#",
  },
  {
    id: 3,
    title: "Neuralink Patient Recovery Stories",
    host: { name: "Dr. Innovation", avatar: "DI", title: "Medical Director" },
    status: "completed",
    scheduledTime: "Completed 3 days ago",
    attendees: 5210,
    tags: ["Neuralink", "Medical", "Innovation"],
    queue: 0,
    description: "Incredible stories from Neuralink patients and breakthrough moments.",
    recordingUrl: "#",
  },
];

export default function Spaces() {
  const [spaces, setSpaces] = useState(mockSpaces);
  const [selectedSpace, setSelectedSpace] = useState<number | null>(null);
  const [liveQuestion, setLiveQuestion] = useState("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="space-y-8">
      {/* Create Space */}
      <motion.div variants={itemVariants} className="border border-border p-6">
        <h3 className="text-lg font-medium mb-4">Host a Space</h3>
        <div className="flex gap-3">
          <Button variant="outline" className="border-border">
            <Calendar className="h-4 w-4 mr-2" />
            Schedule Session
          </Button>
          <Button className="bg-foreground text-background hover:bg-foreground/90">
            <Zap className="h-4 w-4 mr-2" />
            Go Live Now
          </Button>
        </div>
      </motion.div>

      {/* Spaces List */}
      <motion.div
        className="space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {spaces.map((space) => (
          <motion.div
            key={space.id}
            variants={itemVariants}
            className={`border p-6 transition-colors cursor-pointer ${
              selectedSpace === space.id
                ? "border-foreground bg-card"
                : "border-border hover:border-foreground/50"
            }`}
          >
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="text-xs font-medium bg-muted">
                  {space.host.avatar}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-medium text-foreground">
                    {space.title}
                  </h2>
                  <Badge
                    variant={
                      space.status === "live"
                        ? "default"
                        : space.status === "scheduled"
                        ? "secondary"
                        : "outline"
                    }
                    className={space.status === "live" ? "animate-pulse" : ""}
                  >
                    {space.status === "live"
                      ? "🔴 LIVE"
                      : space.status === "scheduled"
                      ? "Scheduled"
                      : "Replay"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {space.host.name} • {space.host.title}
                </p>
              </div>
            </div>

            {/* Info */}
            <p className="text-sm text-muted-foreground mb-4">{space.description}</p>

            {/* Stats */}
            <div className="flex items-center gap-6 mb-4 pb-4 border-b border-border">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Users className="h-4 w-4" />
                {space.attendees.toLocaleString()} attending
              </div>
              <div className="text-xs text-muted-foreground">
                {space.scheduledTime}
              </div>
              {space.queue > 0 && (
                <div className="text-xs font-medium text-foreground">
                  {space.queue} questions in queue
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {space.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {space.status === "live" && (
                <>
                  <Button className="flex-1 bg-foreground text-background hover:bg-foreground/90">
                    <Play className="h-4 w-4 mr-2" />
                    Watch Live
                  </Button>
                  <motion.button
                    onClick={() => setSelectedSpace(selectedSpace === space.id ? null : space.id)}
                    className="px-4 py-2 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors text-sm font-medium"
                  >
                    {selectedSpace === space.id ? "Hide" : "Join"} Queue
                  </motion.button>
                </>
              )}
              {space.status === "scheduled" && (
                <>
                  <Button className="flex-1 bg-foreground text-background hover:bg-foreground/90">
                    <Calendar className="h-4 w-4 mr-2" />
                    Set Reminder
                  </Button>
                  <Button variant="outline" className="flex-1 border-border">
                    View Details
                  </Button>
                </>
              )}
              {space.status === "completed" && (
                <Button className="flex-1 bg-foreground text-background hover:bg-foreground/90">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Recording
                </Button>
              )}
            </div>

            {/* Queue Form */}
            {selectedSpace === space.id && space.status === "live" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-6 pt-6 border-t border-border space-y-4"
              >
                <h4 className="text-sm font-medium">Ask a Question</h4>
                <textarea
                  placeholder="Your question for the host..."
                  value={liveQuestion}
                  onChange={(e) => setLiveQuestion(e.target.value)}
                  rows={3}
                  className="w-full bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-3 resize-none"
                />
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setLiveQuestion("")}>
                    Clear
                  </Button>
                  <Button className="ml-auto bg-foreground text-background hover:bg-foreground/90">
                    Submit Question
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
